import { getFontCss, styleConstants } from '@paywithextend/components'
import { APIProvider } from '@vis.gl/react-google-maps'
import ExtButton from 'ext-ui/components/Button/Button'
import Processing from 'ext-ui/components/Processing/Processing'
import AlertCircleFilledIcon from 'ext-ui/svg/icon/AlertCircleFilledIcon'
import DragHandleIcon from 'ext-ui/svg/icon/DragHandleIcon'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import OnOffSwitch from 'src/components/OnOffSwitch/OnOffSwitch'
import TrashButton from 'src/components/TrashButton/TrashButton'
import { useEnvConfig } from 'src/context/ConfigContext/useConfig'
import { useTranslate } from 'src/localization/Trns'
import { MileageRouteResponse } from 'src/models/expenseReport.model'
import { css } from 'styled-components'

import AddressAutocomplete from './AddressAutocomplete'
import {
  buildStaticMapUrl,
  reconstructStaticMapUrl,
  stripStaticMapUrl,
} from './buildStaticMapUrl'
import MileageMap, {
  MileageLocation,
  MileagePlace,
  RouteComputedData,
} from './MileageMap'
import sectionStyles from './MileageRouteSection.style'

export interface MileageRouteData {
  origin: string
  stops: string[]
  destination: string
  distance: number
  staticMapApiUrl?: string
}

interface MileageRouteSectionProps {
  existingRoute?: MileageRouteResponse
  useMileageRoute?: boolean
  onDistanceComputed: (miles: number) => void
  onRouteDataChanged: (routeData: MileageRouteData | undefined) => void
  onUseMileageRouteChanged: (useMileageRoute: boolean) => void
  children?: React.ReactNode
}

interface WaypointRow {
  id: string
  place: MileageLocation | undefined
}

let nextWaypointId = 0
const makeWaypointId = (): string => `wp-${++nextWaypointId}`

const MileageRouteSection: React.FC<MileageRouteSectionProps> = ({
  existingRoute,
  useMileageRoute = true,
  onDistanceComputed,
  onRouteDataChanged,
  onUseMileageRouteChanged,
  children,
}) => {
  const { GOOGLE_MAPS_API_KEY } = useEnvConfig()
  const translate = useTranslate()

  const [mode, setMode] = useState<'static' | 'interactive'>(
    existingRoute?.staticMapApiUrl ? 'static' : 'interactive',
  )
  const [origin, setOrigin] = useState<MileageLocation | undefined>(
    existingRoute?.origin,
  )
  const [destination, setDestination] = useState<MileageLocation | undefined>(
    existingRoute?.destination,
  )
  const [waypointRows, setWaypointRows] = useState<WaypointRow[]>(
    existingRoute?.stops?.map((stop) => ({
      id: makeWaypointId(),
      place: stop as MileageLocation,
    })) ?? [],
  )
  const [showMap, setShowMap] = useState<boolean>(useMileageRoute)
  const [interactiveMapReady, setInteractiveMapReady] = useState(false)
  const [routeLoading, setRouteLoading] = useState(false)
  const [routeError, setRouteError] = useState<string | undefined>(undefined)
  const dragIndexRef = useRef<number | null>(null)
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null)
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null)

  const staticMapFullUrl = useMemo(
    () =>
      existingRoute?.staticMapApiUrl && GOOGLE_MAPS_API_KEY
        ? reconstructStaticMapUrl(
            existingRoute.staticMapApiUrl,
            GOOGLE_MAPS_API_KEY,
          )
        : undefined,
    [existingRoute?.staticMapApiUrl, GOOGLE_MAPS_API_KEY],
  )

  useEffect(() => {
    if (existingRoute) {
      setMode(existingRoute.staticMapApiUrl ? 'static' : 'interactive')
      setInteractiveMapReady(false)
      setOrigin(existingRoute.origin)
      setDestination(existingRoute.destination)
      setWaypointRows(
        existingRoute.stops?.map((stop) => ({
          id: makeWaypointId(),
          place: stop as MileageLocation,
        })) ?? [],
      )
    }
  }, [existingRoute])

  const switchToInteractive = useCallback((): void => {
    if (mode === 'static') {
      setMode('interactive')
      setInteractiveMapReady(false)
    }
    if (origin && destination) {
      setRouteLoading(true)
    }
    setRouteError(undefined)
  }, [mode, origin, destination])

  const reorderStops = useCallback(
    (fromIndex: number, toIndex: number): void => {
      if (fromIndex === toIndex) return
      setWaypointRows((prev) => {
        const next = [...prev]
        const [moved] = next.splice(fromIndex, 1)
        next.splice(toIndex, 0, moved)
        return next
      })
      switchToInteractive()
    },
    [switchToInteractive],
  )

  const addStop = useCallback((): void => {
    setWaypointRows((prev) => [
      ...prev,
      { id: makeWaypointId(), place: undefined },
    ])
  }, [])

  const removeStop = useCallback(
    (id: string): void => {
      setWaypointRows((prev) => prev.filter((row) => row.id !== id))
      switchToInteractive()
    },
    [switchToInteractive],
  )

  const updateStop = useCallback(
    (id: string, place: MileagePlace | undefined): void => {
      setWaypointRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, place } : row)),
      )
    },
    [],
  )

  const filledWaypoints = useMemo(
    () =>
      waypointRows
        .map((row) => row.place)
        .filter((p): p is MileageLocation => !!p),
    [waypointRows],
  )

  const handleRouteError = useCallback((): void => {
    setRouteLoading(false)
    setRouteError(
      translate(
        'Unable to calculate route. Please check your addresses and try again.',
      ),
    )
  }, [translate])

  const handleRouteComputed = useCallback(
    (data: RouteComputedData): void => {
      setInteractiveMapReady(true)
      setRouteLoading(false)
      setRouteError(undefined)
      onDistanceComputed(data.miles)

      if (!GOOGLE_MAPS_API_KEY || !origin || !destination) return

      const fullStaticMapUrl = buildStaticMapUrl({
        apiKey: GOOGLE_MAPS_API_KEY,
        encodedPolyline: data.encodedPolyline,
        origin: data.resolvedOrigin,
        destination: data.resolvedDestination,
        waypoints: data.resolvedWaypoints,
      })

      const originName = typeof origin === 'string' ? origin : origin.name
      const destinationName =
        typeof destination === 'string' ? destination : destination.name

      onRouteDataChanged({
        origin: originName,
        stops: filledWaypoints.map((wp) =>
          typeof wp === 'string' ? wp : wp.name,
        ),
        destination: destinationName,
        distance: data.miles,
        staticMapApiUrl: fullStaticMapUrl
          ? stripStaticMapUrl(fullStaticMapUrl)
          : undefined,
      })
    },
    [
      onDistanceComputed,
      onRouteDataChanged,
      GOOGLE_MAPS_API_KEY,
      origin,
      destination,
      filledWaypoints,
    ],
  )

  if (!GOOGLE_MAPS_API_KEY) {
    return <>{children}</>
  }

  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <div css={sectionStyles} className="MileageRouteSection">
        <div className="map-toggle-row">
          <span
            css={css`
              ${getFontCss(
                14,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            {translate('Calculate with map')}
          </span>
          <OnOffSwitch
            testId="mileage-map-toggle"
            defaultOnOffState={showMap}
            onChange={(val: boolean): void => {
              setShowMap(val)
              onUseMileageRouteChanged(val)
            }}
            smallMode
          />
        </div>

        {showMap && (
          <>
            <AddressAutocomplete
              fieldLabel={translate('Origin')}
              placeholder={translate('e.g. 123 Main St, New York, NY')}
              value={typeof origin === 'string' ? undefined : origin}
              defaultText={typeof origin === 'string' ? origin : undefined}
              onPlaceSelect={setOrigin}
              onInputChange={switchToInteractive}
              testId="mileage-route-origin"
            />
            {waypointRows.map((row, index) => (
              <div
                key={row.id}
                className={`stop-row${draggingIndex === index ? ' dragging' : ''}${dropTargetIndex === index ? ' drop-target' : ''}`}
                draggable
                onDragStart={(e): void => {
                  dragIndexRef.current = index
                  setDraggingIndex(index)
                  e.dataTransfer.effectAllowed = 'move'
                }}
                onDragOver={(e): void => {
                  e.preventDefault()
                  e.dataTransfer.dropEffect = 'move'
                  if (
                    dragIndexRef.current === null ||
                    dragIndexRef.current === index
                  ) {
                    setDropTargetIndex(null)
                    return
                  }
                  setDropTargetIndex(index)
                }}
                onDragLeave={(): void => {
                  setDropTargetIndex((prev) => (prev === index ? null : prev))
                }}
                onDrop={(e): void => {
                  e.preventDefault()
                  if (dragIndexRef.current !== null) {
                    reorderStops(dragIndexRef.current, index)
                  }
                  dragIndexRef.current = null
                  setDraggingIndex(null)
                  setDropTargetIndex(null)
                }}
                onDragEnd={(): void => {
                  dragIndexRef.current = null
                  setDraggingIndex(null)
                  setDropTargetIndex(null)
                }}
              >
                <div
                  className="drag-handle"
                  aria-label={translate('Drag to reorder')}
                >
                  <DragHandleIcon />
                </div>
                <div className="stop-field">
                  <AddressAutocomplete
                    fieldLabel={translate('Stop')}
                    placeholder={translate('e.g. 456 Elm St, Hartford, CT')}
                    value={
                      typeof row.place === 'string' ? undefined : row.place
                    }
                    defaultText={
                      typeof row.place === 'string' ? row.place : undefined
                    }
                    onPlaceSelect={(p): void => updateStop(row.id, p)}
                    onInputChange={switchToInteractive}
                    testId={`mileage-route-stop-${index}`}
                  />
                </div>
                <TrashButton
                  onClick={(): void => removeStop(row.id)}
                  css={css`
                    align-self: flex-end;
                    margin-bottom: 14px;
                  `}
                />
              </div>
            ))}
            <AddressAutocomplete
              fieldLabel={translate('Destination')}
              placeholder={translate('e.g. 789 Oak Ave, Boston, MA')}
              value={typeof destination === 'string' ? undefined : destination}
              defaultText={
                typeof destination === 'string' ? destination : undefined
              }
              onPlaceSelect={setDestination}
              onInputChange={switchToInteractive}
              testId="mileage-route-destination"
            />
            <ExtButton
              variant="secondary"
              onClick={(): void => {
                addStop()
                switchToInteractive()
              }}
              dropShadow={false}
              data-testid="mileage-route-add-stop"
              css={css`
                align-self: flex-start;
              `}
            >
              {translate('+ Add stop')}
            </ExtButton>
            {routeError && (
              <div
                className="route-error"
                role="alert"
                data-testid="mileage-route-error"
              >
                <AlertCircleFilledIcon />
                <span>{routeError}</span>
              </div>
            )}
          </>
        )}

        {children}
        {showMap && routeLoading && !interactiveMapReady && !routeError && (
          <div
            className="route-loading"
            aria-live="polite"
            data-testid="mileage-route-loading"
          >
            <Processing aria-hidden="true" />
            <span>{translate('Calculating route...')}</span>
          </div>
        )}

        {showMap && mode === 'static' && staticMapFullUrl && (
          <img
            className="static-map-image"
            src={staticMapFullUrl}
            alt={translate('Saved mileage route map')}
          />
        )}

        {showMap && mode === 'interactive' && (
          <div style={{ display: 'grid' }}>
            <div
              style={{
                gridArea: '1 / 1',
                ...((!interactiveMapReady &&
                  staticMapFullUrl && {
                    opacity: 0,
                    pointerEvents: 'none' as const,
                  }) ||
                  {}),
              }}
            >
              <MileageMap
                origin={origin}
                destination={destination}
                waypoints={filledWaypoints}
                onRouteComputed={handleRouteComputed}
                onRouteError={handleRouteError}
              />
            </div>
            {!interactiveMapReady && staticMapFullUrl && (
              <img
                src={staticMapFullUrl}
                alt={translate('Saved mileage route map')}
                style={{
                  gridArea: '1 / 1',
                  width: '100%',
                  height: 300,
                  objectFit: 'cover',
                  borderRadius: 8,
                  display: 'block',
                }}
              />
            )}
          </div>
        )}
      </div>
    </APIProvider>
  )
}

export default MileageRouteSection
